/**
 * Composant Agenda - Calendrier avec événements dynamiques
 * Utilise variablesData passé en props depuis le template
 */

import {
  type DocumentComponent,
  type DocumentProps,
  type VariablesData,
} from "../../lib/types";
import { cn } from "@repo/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { createElement, useState } from "react";
import { Button } from "@repo/ui";

type CalendarEvent = {
  titre: string;
  colorClasses: string;
};

type Events = {
  [key: number]: CalendarEvent[];
};

const EVENT_STYLES = {
  conge: "bg-blue-50 text-blue-700 border border-blue-200",
  conference: "bg-amber-50 text-amber-700 border border-amber-200",
  rdv: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  chantier: "bg-orange-50 text-orange-700 border border-orange-200",
} as const;

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const DAYS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];

function getMonthStart(d: Date) {
  const ms = new Date(d);
  ms.setDate(1);
  const day = ms.getDay();
  const diff = -day + (day === 0 ? -6 : 1) + 1;
  ms.setDate(diff);
  return ms;
}

function AgendaCalendar({ variablesData }: { variablesData: VariablesData }) {
  const todayDate = new Date();
  let start = getMonthStart(todayDate);
  const events: Events = {};

  // Extrait les événements de variablesData
  const secretaireDateDebut = parseInt(variablesData.secretaire_date_debut_conges || "0", 10);
  const secretaireDateFin = parseInt(variablesData.secretaire_date_fin_conges || "0", 10);

  if (!isNaN(secretaireDateDebut) && !isNaN(secretaireDateFin)) {
    for (let i = secretaireDateDebut; i <= secretaireDateFin; i++) {
      events[i] = [{ titre: `Congé ${variablesData.secretaire_prenom || ""} ${variablesData.secretaire_nom || ""}`.trim(), colorClasses: EVENT_STYLES.conge }];
    }
  }

  const conferenceCciDate = parseInt(variablesData.conference_cci_date || "0", 10);
  if (!isNaN(conferenceCciDate)) {
    events[conferenceCciDate] = [{ titre: "Conférence CCI", colorClasses: EVENT_STYLES.conference }];
  }

  const plastitocRdvDate = parseInt(variablesData.plastitoc_rdv_date || "0", 10);
  if (!isNaN(plastitocRdvDate)) {
    events[plastitocRdvDate] = [{ titre: "RDV Plastitoc", colorClasses: EVENT_STYLES.rdv }];
  }

  const livraisonAtelierDate = parseInt(variablesData.livraison_atelier_production_date_prevue || "0", 10);
  if (!isNaN(livraisonAtelierDate)) {
    events[livraisonAtelierDate] = [{ titre: "Fin Chantier Atelier", colorClasses: EVENT_STYLES.chantier }];
  }

  // Rassemble les événements uniques pour la légende
  const legendItems: { titre: string; colorClasses: string }[] = [];
  const seenTitles = new Set<string>();
  for (const dayEvents of Object.values(events)) {
    for (const ev of dayEvents) {
      if (!seenTitles.has(ev.titre)) {
        seenTitles.add(ev.titre);
        legendItems.push(ev);
      }
    }
  }

  const nbMonths = Math.ceil(
    (Math.max(...Object.keys(events).map((e) => parseInt(e)), 0) + todayDate.getDate()) / 30,
  );

  const [displayedMonth, setDisplayedMonth] = useState(0);
  let currentDate: Date;

  if (!nbMonths) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Aucun événement prévu</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Array.from(Array(nbMonths).keys()).map((m) => {
        currentDate = currentDate
          ? currentDate.getDate() > 15
            ? new Date(new Date(currentDate.setDate(1)).setMonth(currentDate.getMonth() + 1))
            : new Date(new Date(currentDate.setDate(1)))
          : new Date(todayDate);

        return (
          <div key={m} className={displayedMonth === m ? "" : "hidden"}>
            {/* Month navigation */}
            <div className="flex items-center justify-between pb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDisplayedMonth(displayedMonth - 1)}
                className={cn(m - 1 < 0 && "invisible")}
                disabled={m - 1 < 0}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Précédent
              </Button>
              <h3 className="text-lg font-semibold text-foreground">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDisplayedMonth(displayedMonth + 1)}
                className={cn(m + 1 >= nbMonths && "invisible")}
                disabled={m + 1 >= nbMonths}
              >
                Suivant
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 rounded-t-surface border border-b-0 border-border bg-card">
              {DAYS.map((jour) => (
                <div
                  key={jour}
                  className={cn(
                    "px-3 py-2 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground",
                  )}
                >
                  {jour}.
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-px rounded-b-surface border border-border bg-border">
              {Array.from(Array(35).keys()).map((rawI) => {
                let i = m * 35 + rawI;
                if (i % 35 === 0) {
                  if (i > 0) {
                    start = getMonthStart(currentDate);
                    i = Math.floor((currentDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                  }
                  currentDate = start;
                } else {
                  currentDate = new Date(start.setDate(start.getDate() + 1));
                }

                const isToday =
                  currentDate.getDate() === todayDate.getDate() &&
                  currentDate.getMonth() === todayDate.getMonth();
                const isWeekend = (rawI + 1) % 7 === 0 || (rawI + 1) % 7 === 6;
                const nbDaysBetween = Math.floor(
                  (currentDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24),
                );

                return (
                  <div
                    key={rawI}
                    className={cn(
                      "h-28 p-1.5 transition-colors",
                      isWeekend ? "bg-muted/50" : "bg-card",
                      rawI === 28 && "rounded-bl-surface",
                      rawI === 34 && "rounded-br-surface",
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <div
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-full text-sm",
                          isToday
                            ? "bg-primary font-semibold text-primary-foreground"
                            : "text-foreground",
                        )}
                      >
                        {currentDate.getDate()}
                      </div>
                      {isToday && (
                        <span className="text-xs font-medium text-primary">
                          Aujourd'hui
                        </span>
                      )}
                    </div>
                    {events[nbDaysBetween]?.map((event, index) => (
                      <div
                        key={index}
                        className={cn(
                          "mt-1 truncate rounded-pastille px-1.5 py-0.5 text-xs font-medium",
                          event.colorClasses,
                        )}
                      >
                        {event.titre}
                      </div>
                    ))}
                  </div>
                );
              })}
              <div className="col-span-4 rounded-br-surface bg-card" />
            </div>
          </div>
        );
      })}

      {/* Legend */}
      {legendItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <span className="text-xs font-medium text-muted-foreground">Légende :</span>
          {legendItems.map((item) => (
            <div
              key={item.titre}
              className={cn("rounded-pastille px-2 py-0.5 text-xs font-medium", item.colorClasses)}
            >
              {item.titre}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Composant Agenda avec métadonnées
 */
export const Agenda: DocumentComponent = function Agenda({
  variablesData,
}: DocumentProps) {
  return createElement(AgendaCalendar, { variablesData });
};

Agenda.metadata = {
  id: "agenda",
  titre: "Agenda",
  icon: "calendar",
};

export default Agenda;
